import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { InfoCard } from "src/design/InfoCard";
import { SnackBar } from "src/design/SnackBar";
import {
  colorTextPrimary,
  colorTextMuted,
  colorTextSecondary,
  colorBorder,
} from "src/design/tokens";
import { RestaurantCapabilityDto } from "src/data/admin/types/restaurantCapability";
import { StaffPermissionDto } from "src/data/admin/types/staffPermission";
import {
  useAttachCapabilityPermissions,
  useDetachCapabilityPermissions,
} from "src/data/admin/queries/restaurantCapability";

interface CapabilityPermissionsPageProps {
  capability: RestaurantCapabilityDto;
  allPermissions: StaffPermissionDto[];
  onBack: () => void;
}

export const CapabilityPermissionsPage = ({
  capability,
  allPermissions,
  onBack,
}: CapabilityPermissionsPageProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(capability.permissionIds),
  );
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    setSelectedIds(new Set(capability.permissionIds));
    setSearch("");
  }, [capability.id]);

  const attachMutation = useAttachCapabilityPermissions();
  const detachMutation = useDetachCapabilityPermissions();
  const isSaving = attachMutation.isPending || detachMutation.isPending;

  const handleToggle = (permissionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) {
        next.delete(permissionId);
      } else {
        next.add(permissionId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const originalIds = new Set(capability.permissionIds);
    const toAttach = [...selectedIds].filter((id) => !originalIds.has(id));
    const toDetach = [...originalIds].filter((id) => !selectedIds.has(id));

    try {
      if (toAttach.length > 0) {
        await attachMutation.mutateAsync({
          capabilityId: capability.id,
          permissionIds: toAttach,
        });
      }
      if (toDetach.length > 0) {
        await detachMutation.mutateAsync({
          capabilityId: capability.id,
          permissionIds: toDetach,
        });
      }
      setSnack({
        open: true,
        message: "Permissions updated successfully.",
        severity: "success",
      });
    } catch (err) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : "An error occurred",
        severity: "error",
      });
    }
  };

  const originalPermissionIds = new Set(capability.permissionIds);

  const attached = allPermissions
    .filter((p) => originalPermissionIds.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const unattached = allPermissions
    .filter((p) => !originalPermissionIds.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const lowerSearch = search.toLowerCase();
  const filteredAttached = attached.filter(
    (p) =>
      p.name.toLowerCase().includes(lowerSearch) ||
      (p.description?.toLowerCase().includes(lowerSearch) ?? false),
  );
  const filteredUnattached = unattached.filter(
    (p) =>
      p.name.toLowerCase().includes(lowerSearch) ||
      (p.description?.toLowerCase().includes(lowerSearch) ?? false),
  );
  const showDivider =
    filteredAttached.length > 0 && filteredUnattached.length > 0;
  const totalFiltered = filteredAttached.length + filteredUnattached.length;

  return (
    <>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: colorTextPrimary }}
            >
              {capability.name}
            </Typography>
          </Box>
          <CustomLoadingButton
            loading={isSaving}
            onSubmit={handleSave}
            label="Save changes"
            color="success"
            fullwidth={false}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Search + count */}
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <TextField
                size="small"
                placeholder="Search permissions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: colorTextMuted, fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 280 } }}
              />
              <Typography variant="body2" sx={{ color: colorTextMuted }}>
                {selectedIds.size} / {allPermissions.length} selected
              </Typography>
            </Box>

            <InfoCard
              title="Permissions"
              icon={<LockOutlinedIcon fontSize="small" />}
            >
              {totalFiltered === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: colorTextMuted }}>
                    {search
                      ? "No permissions match your search."
                      : "No permissions available."}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {filteredAttached.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colorTextSecondary,
                          fontWeight: 600,
                          display: "block",
                          px: 1,
                          pb: 0.5,
                        }}
                      >
                        Attached ({filteredAttached.length})
                      </Typography>
                      <Grid container>
                        {filteredAttached.map((perm) => (
                          <Grid item xs={12} sm={6} key={perm.id}>
                            <PermissionRow
                              permission={perm}
                              checked={selectedIds.has(perm.id)}
                              disabled={isSaving}
                              onToggle={handleToggle}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {showDivider && <Divider sx={{ my: 1.5 }} />}

                  {filteredUnattached.length > 0 && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colorTextMuted,
                          fontWeight: 600,
                          display: "block",
                          px: 1,
                          pb: 0.5,
                        }}
                      >
                        Not attached ({filteredUnattached.length})
                      </Typography>
                      <Grid container>
                        {filteredUnattached.map((perm) => (
                          <Grid item xs={12} sm={6} key={perm.id}>
                            <PermissionRow
                              permission={perm}
                              checked={selectedIds.has(perm.id)}
                              disabled={isSaving}
                              onToggle={handleToggle}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}
            </InfoCard>
          </Grid>
        </Grid>
      </Box>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

interface PermissionRowProps {
  permission: StaffPermissionDto;
  checked: boolean;
  disabled: boolean;
  onToggle: (id: string) => void;
}

const PermissionRow = ({
  permission,
  checked,
  disabled,
  onToggle,
}: PermissionRowProps) => (
  <FormControlLabel
    control={
      <Checkbox
        size="small"
        checked={checked}
        onChange={() => onToggle(permission.id)}
        disabled={disabled}
      />
    }
    label={
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: colorTextPrimary }}
        >
          {permission.name}
        </Typography>
        {permission.description && (
          <Typography
            variant="caption"
            sx={{ color: colorTextMuted, display: "block" }}
          >
            {permission.description}
          </Typography>
        )}
      </Box>
    }
    sx={{
      width: "100%",
      mr: 0,
      py: 0.5,
      px: 1,
      borderRadius: 1,
      "&:hover": { backgroundColor: `${colorBorder}50` },
    }}
  />
);
